import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import SimpleEditorHeader from '@/editor/lib/components/EditorHeader/SimpleEditorHeader';
import { formatAPITimestamp } from '@/utils/dates';
import { TextField, Autocomplete, Button, Switch, FormGroup, FormControlLabel } from '@mui/material';
import { MaterialSymbol } from '@/components/MaterialSymbol';
import FullScreenModal from '@/components/FullScreenModal';
import Container from '../../components/Container';
import { Api } from '../../services/Api';
import { useApi } from '../../hooks/useApi';
import Styles from '../../assets/js/Styles';
import { isDeepEqual } from '../../utils/object';
import { Owner } from '../../declarations/models/Owner';
import { SiteEditor } from './AdminSiteEditor';

export interface OwnerEditorProps {
  ownerId?: number | null;
  ownerName?: string | null;
  onCloseEditor: () => void;
}

export const editorStyling = {
  maxHeight: '100%',
  overflowY: 'auto',
  padding: 2,
  borderRadius: 2,
  backgroundColor: Styles.Colors.FINDER_M24_PRIMARY_HEADER_BACKGROUND,
};

export type OwnerTextFields = 'name' | 'identifier' | 'ekultur_museum_id' | 'dams_integration_enabled';

export const OwnerEditor: FC<OwnerEditorProps> = ({ ownerId, ownerName, onCloseEditor }) => {
  const { t: tCommon } = useTranslation('common');
  const { t: tComp } = useTranslation('components');
  const { enqueueSnackbar } = useSnackbar();
  const [isDirty, setIsDirty] = useState<boolean>(false);
  // const [isReady, setIsReady] = useState<boolean>(false);
  const [savedOwner, setSavedOwner] = useState<Partial<Owner>>();
  const [modifiedOwner, setModifiedOwner] = useState<Partial<Owner>>();
  const [availableOwners, isLoadingAvailableOwners] = useApi(() => Api.getOwners({ rows: 500 }), []);
  const [refreshDep, setRefreshDep] = useState(Date.now);
  const [newOwnerId, setNewOwnerId] = useState<number>();
  const refreshPage = () => setRefreshDep(Date.now());
  const [siteId, setSiteId] = useState<number | null>(null);
  const [editModalOpen, setEditModalOpen] = useState<boolean>(false);

  const handleCloseModal = () => {
    refreshPage();
    setEditModalOpen(false);
  };
  const onAddNewSite = () => {
    // console.log('open owner modal');
    setSiteId(null);
    setEditModalOpen(true);
  };

  const onEditSite = (_siteId: number) => {
    // console.log('open owner modal');
    setSiteId(_siteId);
    setEditModalOpen(true);
  };

  const onAddNewOwner = () => {
    const emptyOwner = {
      name: '',
      parent_identifier: savedOwner?.identifier,
    };
    setSavedOwner(emptyOwner);
  };

  const clearParent = () => {
    setModifiedOwner({
      ...modifiedOwner,
      parent_identifier: null,
      parent: undefined,
    });
  };
  const changeParent = (parent: Owner | null) => {
    if (!parent) {
      return;
    }
    setModifiedOwner({
      ...modifiedOwner,
      parent_identifier: parent.identifier,
    });
  };

  useEffect(() => {
    // console.log('modifiedOwner', modifiedOwner);
  }, [modifiedOwner]);

  useEffect(() => {
    if (ownerId || newOwnerId) {
      const ctx = Api.getOwnerById(newOwnerId || ownerId || 0);
      ctx
        .fetchDirect(null)
        .catch((e) => console.error(e))
        .then((res) => {
          if (res) {
            setSavedOwner(res);
          }
        })
        .finally(ctx.abort);
    }
  }, [ownerId, newOwnerId, refreshDep]);

  useEffect(() => {
    setModifiedOwner(savedOwner);
    // setIsReady(true);
  }, [savedOwner]);

  useEffect(() => setIsDirty(!isDeepEqual(savedOwner, modifiedOwner)), [modifiedOwner, savedOwner]);

  const handleSaveOwner = async () => {
    const empty_required = [];
    if (!modifiedOwner?.name) empty_required.push('Navn');
    if (!modifiedOwner?.identifier) empty_required.push('Signatur');
    if (empty_required.length) {
      enqueueSnackbar(tComp('EmployeeEditor.RequiredFieldsMissing', { fields: empty_required.join(', ') }), {
        variant: 'error',
      });
    } else if (modifiedOwner) {
      // const id = savedOwner?.id;
      // console.log('Save modifiedOwner', modifiedOwner);
      const ctx = Api.saveOwner(modifiedOwner);
      await ctx
        .fetchDirect(null)
        .then((res) => {
          if (res) {
            refreshPage();
            setNewOwnerId(res.id);
            // console.log('savedOwner', savedOwner);
            enqueueSnackbar('Oppdatering vellykket', { variant: 'success' });
          }
        })
        .catch((e) =>
          enqueueSnackbar(tComp('EmployeeEditor.SaveFailed', { error: e.error || e }), { variant: 'error' }),
        );
    }
  };

  const handleFieldChange = (attribute: OwnerTextFields, value: string | boolean) => {
    setModifiedOwner({
      ...modifiedOwner,
      [attribute]: value,
    });
  };

  return (
    <Container fullHeight fullWidth column gap={0}>
      <FullScreenModal modalOpen={editModalOpen}>
        <Container fullWidth left>
          <SiteEditor
            siteId={siteId}
            ownerId={savedOwner?.id}
            ownerName={savedOwner?.name}
            onCloseEditor={handleCloseModal}
          />
        </Container>
      </FullScreenModal>
      <SimpleEditorHeader
        model={savedOwner}
        title={
          savedOwner?.id
            ? `Museum: ${savedOwner.name} ${savedOwner?.parent?.name ? ` (${savedOwner.parent.name})` : ''}`
            : `Nytt nettsted under ${ownerName || '-'}`
        }
        onClose={onCloseEditor}
        onSave={handleSaveOwner}
        isDirty={isDirty}
      />
      <Container fullWidth gap={2} sx={{ marginTop: '16px', height: 'calc(100% -  56px)' }}>
        <Container fullWidth fullHeight left column top gap={2} sx={editorStyling}>
          {savedOwner?.id && (
            <div>
              <p>
                ID: {savedOwner?.id} | Opprettet: {formatAPITimestamp(savedOwner?.created_at)} | Sist endret:{' '}
                {formatAPITimestamp(savedOwner?.updated_at)} <br />
              </p>
              <p>
                <strong>{savedOwner.name}</strong>{' '}
                {savedOwner.parent?.name && (
                  <span>
                    er en del av <strong>{savedOwner.parent.name}</strong> og{' '}
                  </span>
                )}
                {savedOwner.children && (
                  <span>
                    har {savedOwner.children.length} underordnede museer samt {savedOwner.sites?.length} egne nettsteder{' '}
                  </span>
                )}
              </p>
            </div>
          )}
          <Container fullWidth top gap={2}>
            <TextField
              size='small'
              label='Navn'
              fullWidth
              required
              value={modifiedOwner?.name ?? ''}
              onChange={(e) => handleFieldChange('name', e.target.value)}
            />
            <TextField
              size='small'
              label='Signatur'
              fullWidth
              required
              value={modifiedOwner?.identifier ?? ''}
              onChange={(e) => handleFieldChange('identifier', e.target.value)}
            />
            <TextField
              size='small'
              label='Ekultur Museum ID'
              fullWidth
              value={modifiedOwner?.ekultur_museum_id ?? ''}
              onChange={(e) => handleFieldChange('ekultur_museum_id', e.target.value)}
            />
            <div style={{ whiteSpace: 'nowrap' }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={modifiedOwner?.dams_integration_enabled ?? false}
                      onChange={(e) => handleFieldChange('dams_integration_enabled', e.target.checked)}
                      color='secondary'
                    />
                  }
                  label='Aktivér DAMS'
                />
              </FormGroup>
            </div>
          </Container>
          <br />
          Dersom museet er en del av en konsolidering:
          <Container fullWidth top gap={2} mt={1} left>
            <TextField
              size='small'
              label='Foreldre ID'
              sx={{ width: '33%' }}
              required
              disabled
              value={modifiedOwner?.parent_identifier ?? ''}
            />

            <Autocomplete
              value={null}
              getOptionLabel={(option: Owner) => option.name}
              loading={isLoadingAvailableOwners}
              loadingText={tCommon('loading')}
              options={
                Array.isArray(availableOwners)
                  ? []
                  : availableOwners?.owners?.filter((owner: Owner) => !owner.parent_identifier) ?? []
              }
              onChange={(_, newValue: Owner | null) => {
                changeParent(newValue);
              }}
              sx={{ width: '33%' }}
              renderInput={(params) => (
                <TextField {...params} label='Velg eller endre konsolidering (kun toppnivå-eiere)' />
              )}
              slotProps={{
                listbox: {
                  style: {
                    maxHeight: 230,
                  },
                },
              }}
            />

            {savedOwner?.parent_identifier && (
              <Button variant='contained' onClick={() => clearParent()}>
                Nullstill foreldre ID
              </Button>
            )}
          </Container>
          {savedOwner?.id && (
            <>
              <Container fullWidth top gap={0} column left mt={2}>
                <strong>Egne nettsteder ({savedOwner?.sites?.length})</strong>
                <ul>
                  {savedOwner?.sites?.map((site) => (
                    <li key={site.id}>
                      <Button
                        style={{ cursor: 'pointer', textDecoration: 'underline', minWidth: 'none', textAlign: 'left' }}
                        onClick={() => onEditSite(site.id || 0)}>
                        {site.name}
                      </Button>
                    </li>
                  ))}
                </ul>
                <Button
                  variant='contained'
                  onClick={onAddNewSite}
                  color='success'
                  startIcon={<MaterialSymbol name='add' />}>
                  Nytt nettsted under {savedOwner?.name}
                </Button>
              </Container>

              {savedOwner?.parent && savedOwner?.parent.id && (
                <Container fullWidth top gap={0} column left mt={2}>
                  <strong>Forelder</strong>
                  <ul>
                    <li key={savedOwner?.parent.id}>
                      <Button
                        style={{ cursor: 'pointer', textDecoration: 'underline' }}
                        onClick={() => setNewOwnerId(savedOwner?.parent?.id)}>
                        {savedOwner?.parent.name}
                      </Button>
                    </li>
                  </ul>
                </Container>
              )}

              {!savedOwner.parent_identifier && (
                <Container fullWidth top gap={0} column left mt={1}>
                  <strong>Undermuseer ({savedOwner?.children?.length})</strong>
                  <ul>
                    {savedOwner?.children?.map((owner) => (
                      <li key={owner.id}>
                        <Button
                          style={{ cursor: 'pointer', textDecoration: 'underline' }}
                          onClick={() => setNewOwnerId(owner.id)}>
                          {owner.name}
                        </Button>
                      </li>
                    ))}
                  </ul>
                  <Button
                    variant='contained'
                    onClick={onAddNewOwner}
                    color='success'
                    startIcon={<MaterialSymbol name='add' />}>
                    Nytt undermuseum under {savedOwner?.name}
                  </Button>
                </Container>
              )}
            </>
          )}
        </Container>
      </Container>
    </Container>
  );
};

export default OwnerEditor;
